import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFire, faClockRotateLeft, faRotateRight } from '@fortawesome/free-solid-svg-icons';
import Header from '../../components/Header';
import { toast } from 'react-toastify';
import trendingApi from '../../api/trendingApi';
import { LinkSource } from '../../constants/link-source';
import resolveBuyLink, { NO_AFF_LINK_MESSAGE } from '../../utils/resolve-buy-link';
import TrendingPlatformFilter from './trending-platform-filter';
import TrendingHotProductCard from './trending-hot-product-card';
import TrendingFeedCard from './trending-feed-card';

function Trending() {
    const navigate = useNavigate();
    const { isLogined } = useSelector((state) => state.auth);

    const [platform, setPlatform] = useState('all');
    const [hotProducts, setHotProducts] = useState([]);
    const [feedProducts, setFeedProducts] = useState([]);
    const [savedIds, setSavedIds] = useState(new Set());
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [isRedirecting, setIsRedirecting] = useState(false);

    const fetchData = useCallback(async (resetPage = true) => {
        try {
            setIsLoading(true);
            const currentPage = resetPage ? 1 : page;
            const [feedRes, hotRes] = await Promise.all([
                trendingApi.getFeed({ page: currentPage, limit: 20, platform }),
                trendingApi.getHotProducts({ timeWindow: '7d', limit: 10, platform }),
            ]);

            const feedData = feedRes.data?.data;
            const hotData = hotRes.data?.data;

            if (feedData) {
                setFeedProducts(feedData.products || []);
                setHasMore(feedData.pagination?.hasMore || false);
                const saved = new Set();
                (feedData.products || []).forEach((p) => { if (p.isSaved) saved.add(p.productId); });
                setSavedIds(saved);
            }
            if (hotData) setHotProducts(hotData.products || []);
            if (resetPage) setPage(1);
        } catch (err) {
            console.warn('[Trending] fetch error:', err);
        } finally {
            setIsLoading(false);
        }
    }, [platform]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { fetchData(true); }, [platform]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleLoadMore = async () => {
        if (!hasMore || isLoadingMore) return;
        try {
            setIsLoadingMore(true);
            const nextPage = page + 1;
            const res = await trendingApi.getFeed({ page: nextPage, limit: 20, platform });
            const data = res.data?.data;
            if (data) {
                setFeedProducts((prev) => [...prev, ...(data.products || [])]);
                setHasMore(data.pagination?.hasMore || false);
                setPage(nextPage);
            }
        } catch (err) {
            console.warn('[Trending] loadMore error:', err);
        } finally {
            setIsLoadingMore(false);
        }
    };

    const handleBuy = async (product) => {
        if (!isLogined) { navigate('/login'); return; }
        setIsRedirecting(true);
        try {
            const { url, followUpUrl } = await resolveBuyLink({
                platform: product.platform,
                productLink: product.productLink,
                productId: product.productId,
                meta: {
                    productId: product.productId,
                    productName: product.productName,
                    productImage: product.productImage,
                    productPrice: product.productPrice,
                    shopName: product.shopName,
                    commission: product.commission,
                },
            }, LinkSource.Trending);
            window.location.href = url;
            if (followUpUrl) setTimeout(() => { window.location.href = followUpUrl; }, 2500);
        } catch (error) {
            toast.error(error?.response?.data?.message || error?.message || NO_AFF_LINK_MESSAGE);
        } finally {
            setIsRedirecting(false);
        }
    };

    const handleSave = async (product) => {
        if (!isLogined) { navigate('/login'); return; }
        const isSaved = savedIds.has(product.productId);
        setSavedIds((prev) => {
            const next = new Set(prev);
            if (isSaved) next.delete(product.productId);
            else next.add(product.productId);
            return next;
        });
        try {
            if (isSaved) {
                await trendingApi.unsaveProduct(product.productId);
            } else {
                await trendingApi.saveProduct({
                    productId: product.productId,
                    productName: product.productName,
                    productImage: product.productImage,
                    productPrice: product.productPrice,
                    shopName: product.shopName,
                    platform: product.platform,
                    productLink: product.productLink,
                    commission: product.commission,
                });
            }
        } catch {
            setSavedIds((prev) => {
                const next = new Set(prev);
                if (isSaved) next.add(product.productId);
                else next.delete(product.productId);
                return next;
            });
        }
    };

    return (
        <div className="min-h-dvh bg-surface-muted flex flex-col md:w-[768px] lg:w-[1024px] md:mx-auto">
            {/* Redirect loading overlay */}
            {isRedirecting && (
                <div className="fixed inset-0 z-50 bg-black/50 flex flex-col items-center justify-center gap-3">
                    <div className="bg-surface rounded-2xl p-6 flex flex-col items-center gap-3 shadow-xl mx-6">
                        <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
                        <p className="text-sm font-semibold text-content-2">Đang tạo link hoàn tiền...</p>
                        <p className="text-xs text-content-3 text-center">Hệ thống đang tạo link để kích hoạt hoàn tiền cho bạn</p>
                    </div>
                </div>
            )}
            <Header hasBackButton={true} redirectPath="/" />

            <div className="pt-[72px] flex flex-col flex-1">

                {/* Hero banner — compact single row */}
                <div className="mx-3 mt-3 rounded-xl bg-gradient-to-r from-orange-500 via-red-500 to-rose-500 px-4 py-2.5 text-white flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faFire} className="text-yellow-300" />
                        <div>
                            <span className="font-bold text-sm">Xu Hướng</span>
                            <span className="text-white/75 text-xs ml-2">· Top 7 ngày qua</span>
                        </div>
                    </div>
                    <button
                        onClick={() => fetchData(true)}
                        className="flex items-center gap-1 text-xs font-semibold bg-surface/20 hover:bg-surface/30 transition-colors py-1 px-2.5 rounded-full"
                    >
                        <FontAwesomeIcon icon={faRotateRight} size="xs" />
                        Làm mới
                    </button>
                </div>

                {/* Platform filter tabs */}
                <TrendingPlatformFilter platform={platform} onChange={setPlatform} />

                {/* Loading skeleton */}
                {isLoading ? (
                    <div className="flex flex-col gap-3 p-3 mt-3">
                        <div className="flex gap-3 overflow-x-auto pb-1">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="w-40 flex-shrink-0 bg-surface rounded-xl h-52 animate-pulse" />
                            ))}
                        </div>
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="bg-surface rounded-xl h-28 animate-pulse" />
                        ))}
                    </div>
                ) : (
                    <div className="flex flex-col gap-4 pb-8 mt-3">
                        {/* Hot Products */}
                        {hotProducts.length > 0 && (
                            <section>
                                <div className="flex items-center gap-2 px-3 mb-2">
                                    <div className="w-6 h-6 rounded-md bg-amber-100 flex items-center justify-center">
                                        <FontAwesomeIcon icon={faFire} className="text-amber-500 text-xs" />
                                    </div>
                                    <span className="text-sm font-bold text-content">Sản phẩm Hot 🔥</span>
                                    <span className="text-xs text-content-3 ml-auto">7 ngày qua</span>
                                </div>
                                <div className="flex gap-3 overflow-x-auto px-3 pb-2 scrollbar-hide">
                                    {hotProducts.map((product, idx) => (
                                        <TrendingHotProductCard
                                            key={`hot-${product.productId}`}
                                            product={product}
                                            onBuy={handleBuy}
                                            rank={idx + 1}
                                        />
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Feed */}
                        <section>
                            <div className="flex items-center gap-2 px-3 mb-2">
                                <div className="w-6 h-6 rounded-md bg-violet-100 flex items-center justify-center">
                                    <FontAwesomeIcon icon={faClockRotateLeft} className="text-violet-500 text-xs" />
                                </div>
                                <span className="text-sm font-bold text-content">Mua sắm gần đây</span>
                                {feedProducts.length > 0 && (
                                    <span className="text-xs bg-violet-100 text-violet-600 font-semibold px-2 py-0.5 rounded-full ml-auto">
                                        {feedProducts.length}+
                                    </span>
                                )}
                            </div>

                            {feedProducts.length === 0 ? (
                                <div className="mx-3 py-12 text-center bg-surface rounded-xl">
                                    <p className="text-3xl mb-2">🛍️</p>
                                    <p className="text-content-3 text-sm">Chưa có hoạt động mua sắm nào</p>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-2 px-3">
                                    {feedProducts.map((product) => (
                                        <TrendingFeedCard
                                            key={`feed-${product.productId}-${product.lastActivity}`}
                                            product={product}
                                            onBuy={handleBuy}
                                            onSave={handleSave}
                                            isSaved={savedIds.has(product.productId)}
                                        />
                                    ))}
                                    {hasMore && (
                                        <button
                                            onClick={handleLoadMore}
                                            disabled={isLoadingMore}
                                            className="py-2.5 bg-surface rounded-xl border border-line text-sm font-semibold text-primary-600 hover:bg-primary-50 transition-colors disabled:opacity-60"
                                        >
                                            {isLoadingMore ? 'Đang tải...' : 'Xem thêm'}
                                        </button>
                                    )}
                                </div>
                            )}
                        </section>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Trending;
